import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiTrendingUp, FiShoppingCart, FiTarget, FiCode, FiTruck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import HeroCarousel from '../components/HeroCarousel';
import WorldMap, { OFFICE_COORDS, projectOffice } from '../components/WorldMap';
import './Home.css';

const Home = () => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';

  const [services, setServices] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [news, setNews] = useState([]);
  const [pressRefs, setPressRefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, caseStudiesRes, newsRes, pressRes] = await Promise.all([
          axios.get(`/api/services?lang=${lang}`),
          axios.get(`/api/case-studies?featured=true&limit=3&lang=${lang}`),
          axios.get(`/api/news?limit=4&lang=${lang}`),
          axios.get(`/api/press-references?featured=true&lang=${lang}`)
        ]);
        setServices(servicesRes.data.data || []);
        setCaseStudies(caseStudiesRes.data.data || []);
        setNews(newsRes.data.data || []);
        setPressRefs(pressRes.data.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lang]);

  const heroSlideData = t('home.hero.slides', { returnObjects: true });
  const heroSlides = Array.isArray(heroSlideData) ? [
    { id: 1, video: '/videos/slide-1.mp4', link: '/services',             ...heroSlideData[0] },
    { id: 2, video: '/videos/slide-2.mp4', link: '/services#intelligence', ...heroSlideData[1] },
    { id: 3, video: '/videos/slide-3.mp4', link: '/services#operations',   ...heroSlideData[2] },
  ] : [];

  const serviceIcons = {
    'chart-line':    <FiTrendingUp />,
    'shopping-cart': <FiShoppingCart />,
    'bullhorn':      <FiTarget />,
    'code':          <FiCode />,
    'truck':         <FiTruck />
  };

  const stats = t('home.stats', { returnObjects: true });
  const globalOffices = t('home.global.offices', { returnObjects: true });

  // Group press refs by publication name for the "As Featured In" strip
  const featuredPublications = pressRefs
    .filter(p => p.isFeatured)
    .reduce((acc, ref) => {
      const name = ref.publication?.name;
      if (name && !acc.find(a => a.name === name)) {
        acc.push({ name, website: ref.publication.website });
      }
      return acc;
    }, []);

  return (
    <div className="home">
      <HeroCarousel slides={heroSlides} />

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {Array.isArray(stats) && stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">{t('home.services.label')}</p>
            <h2>{t('home.services.title')}</h2>
            <p className="section-description">{t('home.services.description')}</p>
          </div>
          <div className="services-grid">
            {loading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : (
              services.map((service, index) => (
                <div key={service._id || index} className="service-card">
                  <div className="service-icon">
                    {serviceIcons[service.icon] || <FiTrendingUp />}
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {(service.features || []).slice(0, 4).map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                  <Link to={`/services#${service.slug}`} className="service-link">
                    {t('home.services.learnMore')} <FiArrowRight />
                  </Link>
                </div>
              ))
            )}
          </div>
          <div className="section-cta">
            <Link to="/services" className="btn btn-outline">{t('home.services.viewAll')}</Link>
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      <section className="section section-dark case-studies-section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">{t('home.caseStudies.label')}</p>
            <h2>{t('home.caseStudies.title')}</h2>
            <p className="section-description">{t('home.caseStudies.description')}</p>
          </div>
          <div className="case-studies-grid">
            {caseStudies.map((study, index) => (
              <article key={study._id || index} className="case-study-card">
                <div className="case-study-image">
                  <div className="image-placeholder"><span>{study.category}</span></div>
                </div>
                <div className="case-study-content">
                  <span className="case-study-category">{study.category}</span>
                  <h3>{study.title}</h3>
                  <p>{study.excerpt}</p>
                  {study.results && (
                    <div className="case-study-results">
                      {study.results.slice(0, 2).map((result, idx) => (
                        <div key={idx} className="result-item">
                          <span className="result-value">{result.value}</span>
                          <span className="result-label">{result.metric}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Link to={`/case-studies/${study.slug}`} className="case-study-link">
                    {t('home.caseStudies.readStudy')} <FiArrowRight />
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/case-studies" className="btn btn-primary">{t('home.caseStudies.viewAll')}</Link>
          </div>
        </div>
      </section>

      {/* Latest Insights */}
      <section className="section insights-section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">{t('home.insights.label')}</p>
            <h2>{t('home.insights.title')}</h2>
          </div>
          <div className="insights-grid">
            {news.map((item, index) => (
              <article key={item._id || index} className="insight-card">
                <div className="insight-content">
                  <div className="insight-meta">
                    <span className="insight-type">{item.type}</span>
                    <span className="insight-date">
                      {new Date(item.publishedAt).toLocaleDateString(lang === 'ja' ? 'ja-JP' : lang === 'zh-CN' || lang === 'zh-TW' ? 'zh-CN' : 'en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </span>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.excerpt}</p>
                  {item.externalUrl ? (
                    <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" className="insight-link">
                      {t('home.insights.readMore')} <FiArrowRight />
                    </a>
                  ) : (
                    <Link to={`/insights/${item.slug}`} className="insight-link">
                      {t('home.insights.readMore')} <FiArrowRight />
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Press / As Featured In */}
      <section className="section press-section">
        <div className="container">
          <h3 className="press-title">{t('home.press.title')}</h3>
          <div className="press-logos">
            {featuredPublications.length > 0
              ? featuredPublications.map((pub, index) => (
                  <a key={index} href={pub.website} target="_blank" rel="noopener noreferrer" className="press-logo" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {pub.name}
                  </a>
                ))
              : ['McKinsey', 'Harvard Business Review', 'eMarketer', 'TechCrunch', 'Retail Dive'].map((logo, index) => (
                  <div key={index} className="press-logo">{logo}</div>
                ))
            }
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="section section-dark global-section">
        <div className="container">
          <div className="global-content">
            <div className="global-text">
              <p className="section-label">{t('home.global.label')}</p>
              <h2>{t('home.global.title')}</h2>
              <p>{t('home.global.description')}</p>
              <Link to="/about" className="btn btn-primary">{t('home.global.learnAbout')}</Link>
            </div>
            <div className="global-map">
              <div className="map-placeholder">
                <WorldMap />
                {Array.isArray(globalOffices) && globalOffices.map((office) => {
                  const coords = OFFICE_COORDS[office.id];
                  if (!coords) return null;
                  return (
                    <div key={office.id} className="map-dot" style={projectOffice(coords)}>
                      <span>{office.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
